<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import {
  LayoutDashboard, FolderKanban, Package,
  FileText, TrendingUp, BarChart2, Settings,
  Bell, Search, ChevronLeft, ChevronRight, Sun, Moon,
  ScanBarcode, Menu, X, BookOpen, Star, Radio,
} from 'lucide-vue-next'
import { getVisibleNavGroups } from '@/lib/permissions'
import LogoMark from '@/components/brand/LogoMark.vue'
import CommandPalette from '@/components/ui/CommandPalette.vue'
import { useTheme } from '@/composables/useTheme'
import { useAuthStore } from '@/stores/auth'
import { useStarredProjects } from '@/composables/useStarredProjects'
import { api } from '@/lib/api'
import { useRouter } from 'vue-router'

const collapsed = ref(false)
const mobileOpen = ref(false)
const showPalette = ref(false)
const appVersion = __APP_VERSION__
const { theme, toggle } = useTheme()
const auth = useAuthStore()
const router = useRouter()

function onKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    showPalette.value = !showPalette.value
  }
}

// Close mobile sidebar on route change
const route = useRoute()
watch(() => route.path, () => { mobileOpen.value = false })

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

async function handleLogout() {
  await auth.logout()
  router.push({ name: 'login' })
}

const iconMap: Record<string, any> = {
  LayoutDashboard, FolderKanban, ScanBarcode, Package, FileText, TrendingUp, BarChart2, Settings, BookOpen, Radio,
}

const navGroups = computed(() =>
  getVisibleNavGroups(auth.user?.role).map(g => ({
    label: g.label,
    beta: (g as any).beta === true,
    items: g.items.map(i => ({ ...i, icon: iconMap[i.iconName] })),
  }))
)

const active = (path: string) =>
  path === '/' ? route.path === '/' : route.path.startsWith(path)

// #71: Starred projects in the sidebar. Fetches the user's starred IDs and
// joins against the projects list (already cached by other views). Renders
// alphabetically; hides the group when empty. Both queries gated on auth.
const stars = useStarredProjects()
const projectsQuery = useQuery({
  queryKey: ['projects', {}],
  queryFn: () => api.projects.list(),
  enabled: computed(() => auth.isAuthenticated && stars.starredIds.value.length > 0),
  retry: false,
  staleTime: 60_000,
})
const starredProjects = computed(() => {
  const all = projectsQuery.data.value ?? []
  const ids = new Set(stars.starredIds.value)
  return all
    .filter(p => ids.has(p.id))
    .sort((a, b) => a.name.localeCompare(b.name))
})
</script>

<template>
  <CommandPalette v-if="showPalette" @close="showPalette = false" />

  <div class="flex h-screen overflow-hidden" :style="{ background: 'var(--app-bg)' }">

    <!-- ── Mobile overlay ── -->
    <transition name="fade">
      <div
        v-if="mobileOpen"
        class="fixed inset-0 z-30 bg-black/50 lg:hidden"
        @click="mobileOpen = false"
      />
    </transition>

    <!-- ── Sidebar ────────────────────────────────────────── -->
    <aside
      :class="[
        'flex flex-col shrink-0 z-40 transition-all duration-200 ease-in-out',
        // Desktop: inline, collapsible
        'max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:w-sidebar',
        mobileOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full',
        // Desktop sizing
        'lg:relative', collapsed ? 'lg:w-[52px]' : 'lg:w-sidebar',
      ]"
      style="background: var(--sidebar-bg); border-right: 1px solid var(--sidebar-border);"
    >
      <!-- Brand -->
      <div
        class="flex items-center gap-2.5 h-14 px-3.5 shrink-0 overflow-hidden"
        style="border-bottom: 1px solid var(--sidebar-border);"
      >
        <LogoMark class="w-6 h-6 shrink-0 rounded object-cover" />
        <div v-if="!collapsed" class="flex flex-col leading-none">
          <span class="text-sm font-semibold text-white tracking-tight">Skylark</span>
          <span class="text-2xs text-white/50 tracking-widest uppercase">Northstar</span>
        </div>
      </div>

      <!-- Nav -->
      <nav class="flex-1 overflow-y-auto overflow-x-hidden py-3 space-y-4">
        <!-- #71: Starred projects (hidden when empty). -->
        <div v-if="starredProjects.length > 0">
          <p
            v-if="!collapsed"
            class="px-3.5 mb-1 select-none"
            style="font-size:0.6rem; font-weight:700; color:var(--sidebar-text-muted); letter-spacing:0.1em; text-transform:uppercase;"
          >Starred</p>
          <ul class="space-y-px px-1.5">
            <li v-for="p in starredProjects" :key="p.id">
              <RouterLink
                :to="`/projects/${p.id}`"
                :title="collapsed ? `${p.code} — ${p.name}` : undefined"
                class="nav-item relative items-center"
                :class="[collapsed ? 'justify-center' : '', active(`/projects/${p.id}`) ? 'nav-item-active' : '']"
              >
                <span
                  v-if="active(`/projects/${p.id}`)"
                  class="absolute left-0 inset-y-2 w-[2px] rounded-r-full"
                  style="background:#74b743; margin-left:-6px;"
                />
                <Star :size="14" class="shrink-0 text-warning-base" fill="currentColor" :stroke-width="0" />
                <span v-if="!collapsed" class="min-w-0 flex-1">
                  <span class="block text-2xs font-mono text-white/70">{{ p.code }}</span>
                  <span class="block text-xs leading-snug">{{ p.name }}</span>
                </span>
              </RouterLink>
            </li>
          </ul>
        </div>

        <div v-for="group in navGroups" :key="group.label">
          <p
            v-if="!collapsed"
            class="px-3.5 mb-1 select-none flex items-center gap-1.5"
            style="font-size:0.6rem; font-weight:700; color:var(--sidebar-text-muted); letter-spacing:0.1em; text-transform:uppercase;"
          >
            <span>{{ group.label }}</span>
            <span
              v-if="group.beta"
              class="px-1 rounded"
              style="font-size:0.5rem; letter-spacing:0.08em; color:#74b743; border:1px solid rgba(116,183,67,0.4); background:transparent;"
            >BETA</span>
          </p>

          <ul class="space-y-px px-1.5">
            <li v-for="item in group.items" :key="item.name">
              <RouterLink
                :to="item.to"
                :title="collapsed ? item.name : undefined"
                class="nav-item relative"
                :class="[collapsed ? 'justify-center' : '', active(item.to) ? 'nav-item-active' : '']"
              >
                <span
                  v-if="active(item.to)"
                  class="absolute left-0 inset-y-2 w-[2px] rounded-r-full"
                  style="background:#74b743; margin-left:-6px;"
                />
                <component :is="item.icon" :size="15" class="shrink-0" :stroke-width="active(item.to) ? 2.25 : 1.75" />
                <span v-if="!collapsed" class="truncate leading-none">{{ item.name }}</span>
              </RouterLink>
            </li>
          </ul>
        </div>
      </nav>

      <!-- User -->
      <div class="shrink-0 p-2" style="border-top: 1px solid var(--sidebar-border);">
        <div
          :class="['flex items-center gap-2 rounded-md p-2 cursor-pointer transition-colors', collapsed ? 'justify-center' : '']"
          style="color: var(--sidebar-text);"
          @mouseenter="($event.currentTarget as HTMLElement).style.background='var(--sidebar-hover)'"
          @mouseleave="($event.currentTarget as HTMLElement).style.background='transparent'"
          @click="handleLogout"
          :title="collapsed ? `${auth.user?.name ?? ''} — click to sign out` : 'Click to sign out'"
        >
          <div
            class="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-2xs font-bold"
            style="background:rgba(116,183,67,0.2); color:#74b743;"
          >{{ auth.initials }}</div>
          <div v-if="!collapsed" class="flex-1 min-w-0">
            <p class="text-xs font-medium leading-none mb-0.5 text-white truncate">{{ auth.user?.name ?? '—' }}</p>
            <p class="text-2xs truncate" style="color:var(--sidebar-text);">{{ auth.user?.role?.replace(/_/g, ' ') }} · {{ auth.user?.location ?? '' }}</p>
          </div>
        </div>
        <p
          v-if="!collapsed"
          class="px-2 pt-1 text-2xs font-mono truncate select-none"
          style="color: var(--sidebar-text); opacity: 0.55;"
          :title="`Revision ${appVersion}`"
        >Rev {{ appVersion }}</p>
      </div>

      <!-- Collapse toggle (desktop only) -->
      <button
        type="button"
        class="absolute -right-3 top-[4.25rem] z-30 hidden lg:flex items-center justify-center w-5 h-5 rounded-full transition-colors"
        style="background:var(--sidebar-bg); border:1px solid var(--sidebar-border); color:var(--sidebar-text);"
        @click="collapsed = !collapsed"
      >
        <ChevronLeft v-if="!collapsed" :size="11" />
        <ChevronRight v-else :size="11" />
      </button>

      <!-- Close button (mobile only) -->
      <button
        type="button"
        class="absolute top-3 right-3 z-30 flex lg:hidden items-center justify-center w-7 h-7 rounded-full transition-colors"
        style="background:var(--sidebar-hover); color:var(--sidebar-text-active);"
        @click="mobileOpen = false"
      >
        <X :size="15" />
      </button>
    </aside>

    <!-- ── Main content ───────────────────────────────────── -->
    <div class="flex flex-col flex-1 min-w-0 overflow-hidden">

      <!-- Glass header -->
      <header
        class="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 h-14 shrink-0"
        style="
          background: var(--header-bg);
          border-bottom: 1px solid var(--header-border);
          box-shadow: var(--header-shadow);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        "
      >
        <!-- Hamburger (mobile) -->
        <button
          type="button"
          class="btn btn-ghost btn-icon lg:hidden shrink-0"
          @click="mobileOpen = true"
        >
          <Menu :size="18" style="color:var(--ink-secondary);" />
        </button>

        <!-- Search -->
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all hover:border-ink-disabled/50 min-w-0 flex-1 sm:flex-initial"
          style="
            background: var(--surface-sunken);
            border: 1px solid var(--surface-border);
            color: var(--ink-disabled);
            backdrop-filter: blur(8px);
          "
          :style="{ maxWidth: '320px' }"
          @click="showPalette = true"
        >
          <Search :size="13" class="shrink-0" />
          <span class="flex-1 text-left truncate">Search or jump to SKY-…</span>
          <kbd class="ml-auto text-2xs px-1.5 py-0.5 rounded font-mono hidden sm:inline" style="background:var(--surface-border); color:var(--ink-tertiary);">⌘K</kbd>
        </button>

        <div class="ml-auto flex items-center gap-1">
          <!-- Theme toggle -->
          <button type="button" class="btn btn-ghost btn-icon" @click="toggle" :title="theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'">
            <Sun v-if="theme === 'dark'" :size="15" style="color:var(--ink-tertiary);" />
            <Moon v-else :size="15" style="color:var(--ink-tertiary);" />
          </button>

          <!-- Notifications -->
          <button type="button" class="btn btn-ghost btn-icon relative">
            <Bell :size="15" style="color:var(--ink-tertiary);" />
            <!-- Ping ring + solid dot -->
            <span class="absolute top-1.5 right-1.5 flex w-2 h-2">
              <span class="absolute inline-flex w-full h-full rounded-full animate-ping" style="background:#74b743; opacity:0.5;" />
              <span class="relative w-2 h-2 rounded-full" style="background:#74b743;" />
            </span>
          </button>

          <slot name="header-actions" />
        </div>
      </header>

      <!-- Page -->
      <main class="flex-1 overflow-y-auto">
        <slot />
      </main>
    </div>
  </div>
</template>
